import React from 'react';
import PropTypes from 'prop-types';
import {
  Heading_1 as Heading1,
  useThemeContext
} from '@huggg/react-components';

import styles from './segments.module.scss';
import { connect } from 'react-redux';
import { withHuggg } from 'hoc';
import { compose } from 'redux';
import SegmentCard from './SegmentCard';
import { getSegments } from 'redux/modules/huggg';
import classNames from 'classnames';
// pixels-per-animation-frame
const SCROLL_RATE = 20;

const SVGArrow = ({ points }) => (
  <svg
    width="24px"
    height="24px"
    viewBox="0 0 24 24"
    strokeWidth="2"
    stroke="currentColor"
    fill="none"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path stroke="none" d="M0 0h24v24H0z" fill="none" />
    <polyline points={points} />
  </svg>
);
SVGArrow.propTypes = { points: PropTypes.string.isRequired };

export const SegmentCarouselUnconnected = props => {
  const { huggg, segments, onSelectSegment } = props;
  const carouselRef = React.useRef(null);
  const scrolling = React.useRef(null);
  const observer = React.useRef(null);

  const [overflow, setOverflow] = React.useState(0);

  const { name: theme } = useThemeContext();

  const redeemMessage =
    theme === 'welfare'
      ? 'Please select the voucher you would like to redeem.'
      : 'Take your pick:';

  const doScroll = modify => {
    const change = modify(carouselRef.current.scrollLeft, SCROLL_RATE);

    if (change > overflow || change < 0) {
      return;
    }

    carouselRef.current.scrollLeft = change;
    scrolling.current = window.requestAnimationFrame(() => doScroll(modify));
  };

  const scrollLeft = () => {
    doScroll((a, b) => a - b);
  };

  const scrollRight = () => {
    doScroll((a, b) => a + b);
  };

  const stopScroll = () => {
    window.cancelAnimationFrame(scrolling.current);
  };

  const calculateOverflow = (currentRef, setOverflow) => {
    const { scrollWidth, offsetWidth } = currentRef;

    setOverflow(scrollWidth - offsetWidth);
  };

  React.useEffect(() => {
    if (!carouselRef.current) {
      return;
    }

    calculateOverflow(carouselRef.current, setOverflow);

    observer.current = new ResizeObserver(() =>
      calculateOverflow(carouselRef.current, setOverflow)
    );
    observer.current.observe(carouselRef.current);

    return () => {
      observer.current.unobserve(carouselRef.current);
      window.cancelAnimationFrame(scrolling.current);
    };
  }, []);

  return (
    <>
      <div className={styles.redeemMessage}>
        <Heading1>{redeemMessage}</Heading1>
      </div>
      <div className={styles.segments} data-test-id="segment-options">
        <div className={styles.segmentWrapper}>
          <div
            data-test-id="segment-carousel"
            className={styles.segmentCarousel}
            ref={carouselRef}
          >
            {segments.map((segment, index) => (
              <div
                key={segment.id}
                data-test-id={`segment-${index}`}
                style={{ width: '100%', height: '100%' }}
              >
                <SegmentCard
                  data-test-id={`segment-card-${index}`}
                  segment={segment}
                  huggg={huggg}
                  shadow={true}
                  inSlider={true}
                  chooseSegment={() => onSelectSegment(segment)}
                />
              </div>
            ))}
            {overflow ? (
              <>
                <div
                  onTouchStart={scrollLeft}
                  onMouseDown={scrollLeft}
                  onTouchEnd={stopScroll}
                  onMouseUp={stopScroll}
                  onMouseLeave={stopScroll}
                  className={classNames(
                    styles.carouselArrow,
                    styles.carouselArrowPrev
                  )}
                >
                  <SVGArrow points="15 6 9 12 15 18" />
                </div>
                <div
                  onTouchStart={scrollRight}
                  onMouseDown={scrollRight}
                  onTouchEnd={stopScroll}
                  onMouseUp={stopScroll}
                  onMouseLeave={stopScroll}
                  className={classNames(
                    styles.carouselArrow,
                    styles.carouselArrowNext
                  )}
                >
                  <SVGArrow points="9 6 15 12 9 18" />
                </div>
              </>
            ) : null}
          </div>
        </div>
      </div>
    </>
  );
};

SegmentCarouselUnconnected.propTypes = {
  huggg: PropTypes.object,
  segments: PropTypes.array,
  onSelectSegment: PropTypes.func
};

const mapStateToProps = state => {
  return {
    segments: getSegments(state)
  };
};

export default compose(
  withHuggg,
  connect(mapStateToProps)
)(SegmentCarouselUnconnected);
